import { useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import {
  Color,
  MagnifyingGlassIcon,
  TextFieldController,
  WithPopperSimple
} from "@bonliva-crm/ui";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography,
  makeStyles
} from "@material-ui/core";
import { AddCircle } from "@material-ui/icons";
import locale from "@bonliva-crm/locale";
import { CallListType } from "@bonliva-crm/shared";
import { CONSULTANT_SLIDER, ConsultantEditorLocation } from "@navigation";
import { store } from "@store";
import { WithCallListGlobalModal } from "@consultants/consultant-profile-new/call-list-global-modal/with-call-list-global-modal";
import { GlobalCallListModal } from "@consultants/consultant-profile-new/call-list-global-modal/global-call-list-modal";
import { FilterSlider } from "@bonliva-crm/ui/search-filters";
import { ConsultantTable } from "./components/consultant-table/consultant-table";
import { FavouriteFilter } from "@bonliva-crm/ui/search-filters";
import { CreateListMenu } from "./components/create-list-menu";
import { useConsultantSearchFilters } from "./hooks/use-consultant-search-filters";
import { NewListModal } from "../consultant-profile-new/call-list/components/new-list-modal";
import { SearchProfileType } from "@bonliva-crm/graphql";

export const ConsultantSearchNew = () => {
  const classes = useStyles();
  const { navigation } = store();
  const [createListModal, setCreateListModal] = useState<CallListType | null>();
  const { filterManager } = useConsultantSearchFilters();

  const handleCreateList = (type: CallListType) => {
    setCreateListModal(type);
  };

  return (
    <WithCallListGlobalModal>
      <FormProvider {...filterManager.formMethods}>
        <Box className={classes.root}>
          <Box className={classes.flexColumn}>
            <Box className={classes.flexRow} style={{ justifyContent: "space-between" }}>
              <Typography variant="h2">{locale.Consultants}</Typography>

              <Box className={classes.flexRow} flexGrow={1}>
                <TextFieldController
                  name="text"
                  textFieldProps={{
                    className: classes.searchField,
                    placeholder: locale.Search,
                    inputProps: { autoComplete: "off" },
                    InputProps: {
                      startAdornment: <MagnifyingGlassIcon />,
                      style: { borderColor: Color.DarkGray }
                    }
                  }}
                />
              </Box>
              <Box className={classes.flexRow}>
                <Controller
                  control={filterManager.formMethods.control}
                  name="fullTextSearch"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={locale.IncludeComments}
                    />
                  )}
                />

                <WithPopperSimple
                  popperContent={({ close }) => (
                    <CreateListMenu
                      onCreateCallList={() => {
                        handleCreateList(CallListType.CallList);
                        close();
                      }}
                      onCreateContactList={() => {
                        handleCreateList(CallListType.ContactList);
                        close();
                      }}
                    />
                  )}
                >
                  {({ open }) => (
                    <Button variant="outlined" color="primary" onClick={open}>
                      {locale.CreateList}
                    </Button>
                  )}
                </WithPopperSimple>

                <Button
                  variant="contained"
                  startIcon={<AddCircle />}
                  onClick={() =>
                    navigation.open(CONSULTANT_SLIDER, new ConsultantEditorLocation(null))
                  }
                >
                  {locale.NewConsultant}
                </Button>
              </Box>
            </Box>

            <FavouriteFilter
              filterManager={filterManager}
              searchProfileType={SearchProfileType.ConsultantsPipeline}
            />

            <Divider />

            <ConsultantTable />
          </Box>
          <FilterSlider
            filterManager={filterManager}
            searchProfileType={SearchProfileType.ConsultantsPipeline}
          />
        </Box>

        <NewListModal
          open={!!createListModal}
          onClose={() => setCreateListModal(null)}
          type={createListModal}
        />
      </FormProvider>

      <GlobalCallListModal />
    </WithCallListGlobalModal>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2, 4, 5.2, 2),
    height: "100%"
  },
  flexColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    height: "100%"
  },
  flexRow: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2)
  },
  searchField: {
    width: "80%",
    marginLeft: "auto"
  },
  allFilters: {
    padding: "0 1rem",
    border: `1px solid ${Color.LightGray}`,
    height: "2.5rem",
    borderRadius: "0.375rem",
    width: "125px",
    "&:hover": {
      cursor: "pointer"
    }
  }
}));
